/**
 * R-11 — Central Gemini call tracer: llmTraces + billing/dailyAggregates.
 *
 * Cost recording is fire-and-forget — never blocks or fails the Gemini call.
 * Optional hard block (ENABLE_HARD_BLOCK=true, default OFF) runs pre-flight.
 */
import { computeCallCost, sanitizeModelKey } from '../billing/geminiCostModel';
import { readGeminiUsage } from '../billing/geminiUsage';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import { stripUndefined } from '../firestore/stripUndefined';
import { logAuditEvent } from './auditTelemetry';
import { COST_ALARM_CONFIG } from '../billing/costAlarmDispatcher';
import { getDailyCostForUser } from '../billing/dailyAggregate';

/** Thrown only when ENABLE_HARD_BLOCK=true and daily budget exceeded. */
export class GeminiBudgetExceededError extends Error {
  readonly code = 'GEMINI_BUDGET_EXCEEDED';
  readonly uid: string;
  readonly totalCostUsd: number;
  readonly thresholdUsd: number;

  constructor(uid: string, totalCostUsd: number, thresholdUsd: number) {
    super(
      `Gemini daily budget exceeded for UID ${uid}: $${totalCostUsd.toFixed(2)} >= $${thresholdUsd}`,
    );
    this.name = 'GeminiBudgetExceededError';
    this.uid = uid;
    this.totalCostUsd = totalCostUsd;
    this.thresholdUsd = thresholdUsd;
  }
}

export function isHardBlockEnabled(): boolean {
  const raw = typeof process !== 'undefined'
    ? process.env?.ENABLE_HARD_BLOCK
    : (import.meta as { env?: Record<string, string> }).env?.VITE_ENABLE_HARD_BLOCK;
  return raw === 'true' || raw === '1';
}

export interface TracedGeminiCallContext {
  uid?: string;
  companyId?: string | null;
  siteId?: string;
  reportId?: string;
  reportType?: string;
  stage?: string;
  sessionId?: string;
}

export interface TracedGeminiCallResult {
  /** Priced on promptTokens (input) + responseTokens + thoughtsTokens (output). */
  costUsd: number;
  promptTokens: number;
  /** Visible response tokens (`candidatesTokenCount`); EXCLUDES thinking. */
  responseTokens: number;
  /** Thinking tokens (`thoughtsTokenCount`), billed at the output rate. */
  thoughtsTokens: number;
  totalTokens: number;
  model: string;
}

function utcDateKey(d = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Token usage of one response. `responseTokens` keeps its historical meaning
 * (visible response only) so stored llmTraces / billingDailyUsers rows stay
 * comparable; thinking is carried separately as `thoughtsTokens` and both are
 * priced at the output rate (see lib/billing/geminiUsage.ts).
 */
function extractUsage(response: unknown): {
  promptTokens: number;
  responseTokens: number;
  thoughtsTokens: number;
  billableOutputTokens: number;
  totalTokens: number;
} {
  const usage = readGeminiUsage(
    (response as { usageMetadata?: unknown } | null)?.usageMetadata,
  );
  return {
    promptTokens: usage.promptTokens,
    responseTokens: usage.candidatesTokens,
    thoughtsTokens: usage.thoughtsTokens,
    billableOutputTokens: usage.billableOutputTokens,
    totalTokens: usage.totalTokens,
  };
}

async function resolveAuthContext(
  ctx: TracedGeminiCallContext,
): Promise<{ uid?: string; companyId?: string | null }> {
  if (ctx.uid) {
    return { uid: ctx.uid, companyId: ctx.companyId ?? null };
  }
  try {
    const { auth } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) return {};
    const token = await user.getIdTokenResult();
    return {
      uid: user.uid,
      companyId: (token.claims.companyId as string | undefined) ?? ctx.companyId ?? null,
    };
  } catch {
    return {};
  }
}

/** Pre-flight budget check — only throws when hard block is enabled. */
export async function assertGeminiBudgetAllowsCall(
  ctx: TracedGeminiCallContext = {},
): Promise<void> {
  if (!isHardBlockEnabled()) return;

  const { uid } = await resolveAuthContext(ctx);
  if (!uid) return;

  const today = utcDateKey();
  const aggregate = await getDailyCostForUser(uid, today);
  const spent = aggregate?.totalCostUsd ?? 0;
  const threshold = COST_ALARM_CONFIG.blockPerUserUsd;

  if (spent >= threshold) {
    throw new GeminiBudgetExceededError(uid, spent, threshold);
  }
}

/** Post-call: llmTraces row + atomic billing increment. Never throws. */
export async function recordTracedGeminiCall(
  model: string,
  response: unknown,
  ctx: TracedGeminiCallContext = {},
  startedAt?: number,
): Promise<TracedGeminiCallResult | null> {
  try {
    const {
      promptTokens, responseTokens, thoughtsTokens, billableOutputTokens, totalTokens,
    } = extractUsage(response);
    let costUsd = 0;
    try {
      // Thinking tokens are billed at the output rate — price response + thoughts.
      costUsd = computeCallCost(model, promptTokens, billableOutputTokens);
    } catch (err) {
      console.warn('[tracedGeminiCall] unknown model — skipping cost:', model, (err as Error)?.message);
      return null;
    }

    const { uid, companyId } = await resolveAuthContext(ctx);
    const date = utcDateKey();
    const modelKey = sanitizeModelKey(model);
    const latencyMs = startedAt != null ? Date.now() - startedAt : undefined;

    logAuditEvent({
      type: 'gemini-call-cost-computed',
      siteId: ctx.siteId,
      reportId: ctx.reportId,
      reportType: ctx.reportType,
      payload: {
        model,
        costUsd,
        promptTokens,
        responseTokens,
        thoughtsTokens,
        totalTokens,
        stage: ctx.stage,
        latencyMs,
      },
    });

    void persistLlmTrace({
      userId: uid,
      customerId: companyId,
      model,
      costUsd,
      promptTokens,
      responseTokens,
      thoughtsTokens,
      totalTokens,
      latencyMs,
      stage: ctx.stage,
      siteId: ctx.siteId,
      reportId: ctx.reportId,
      reportType: ctx.reportType,
    });

    if (uid) {
      void incrementDailyAggregate({
        uid,
        companyId,
        date,
        modelKey,
        costUsd,
        promptTokens,
        responseTokens,
        thoughtsTokens,
        totalTokens,
      });
    }

    return { costUsd, promptTokens, responseTokens, thoughtsTokens, totalTokens, model };
  } catch (err) {
    console.warn('[tracedGeminiCall] record failed:', (err as Error)?.message);
    return null;
  }
}

async function persistLlmTrace(row: Record<string, unknown>): Promise<void> {
  try {
    const { auth, db } = await import('../../firebase');
    if (!auth.currentUser && !row.userId) return;
    const { collection, addDoc } = await import('firebase/firestore');
    await addDoc(
      collection(db, 'llmTraces'),
      stripUndefined({
        ...row,
        recordedAt: new Date().toISOString(),
      }),
    );
  } catch (err) {
    console.warn('[tracedGeminiCall] llmTraces write failed:', (err as Error)?.message);
  }
}

async function incrementDailyAggregate(args: {
  uid: string;
  companyId?: string | null;
  date: string;
  modelKey: string;
  costUsd: number;
  promptTokens: number;
  responseTokens: number;
  thoughtsTokens: number;
  totalTokens: number;
}): Promise<void> {
  try {
    const { db } = await import('../../firebase');
    const { doc, setDoc, increment, serverTimestamp } = await import('firebase/firestore');
    // R-130-9: canonical flat billing aggregate path (one doc per date+uid).
    const aggregateDocId = `${args.date}__${args.uid}`;
    const ref = doc(db, 'billingDailyUsers', aggregateDocId);
    await setDoc(
      ref,
      {
        uid: args.uid,
        companyId: args.companyId ?? null,
        date: args.date,
        totalCostUsd: increment(args.costUsd),
        callCount: increment(1),
        promptTokens: increment(args.promptTokens),
        responseTokens: increment(args.responseTokens),
        thoughtsTokens: increment(args.thoughtsTokens),
        totalTokens: increment(args.totalTokens),
        [`byModel.${args.modelKey}.costUsd`]: increment(args.costUsd),
        [`byModel.${args.modelKey}.callCount`]: increment(1),
        [`byModel.${args.modelKey}.promptTokens`]: increment(args.promptTokens),
        [`byModel.${args.modelKey}.responseTokens`]: increment(args.responseTokens),
        [`byModel.${args.modelKey}.thoughtsTokens`]: increment(args.thoughtsTokens),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('[tracedGeminiCall] daily aggregate increment failed:', (err as Error)?.message);
  }
}

/** Nesting guard — inner generateContent calls skip duplicate metering. */
let traceDepth = 0;

/**
 * Wrap an arbitrary Gemini SDK call with budget pre-check + post-call metering.
 */
export async function tracedGeminiCall<T>(
  model: string,
  fn: () => Promise<T>,
  ctx: TracedGeminiCallContext = {},
): Promise<T> {
  await assertGeminiBudgetAllowsCall(ctx);
  traceDepth += 1;
  const startedAt = Date.now();
  try {
    const result = await fn();
    void recordTracedGeminiCall(model, result, ctx, startedAt);
    return result;
  } catch (error) {
    // The Argon completion guard fails closed but retains the provider response
    // so billed usage is still recorded before the incomplete result is rethrown.
    const incomplete = error as { name?: unknown; response?: unknown };
    if (incomplete?.name === 'GeminiIncompleteResponseError' && incomplete.response) {
      void recordTracedGeminiCall(model, incomplete.response, ctx, startedAt);
    }
    throw error;
  } finally {
    traceDepth -= 1;
  }
}

/** Install a generateContent wrapper on the shared SDK client (idempotent). */
export function installGeminiCallTracer(): void {
  const sentinel = '__geminiCallTracerInstalled';
  const g = globalThis as Record<string, unknown>;
  if (g[sentinel]) return;

  void (async () => {
    try {
      const { ai } = await import('../../services/geminiClient');
      type GenerateContentFn = typeof ai.models.generateContent;
      const models = ai.models as typeof ai.models & {
        __origGenerateContent?: GenerateContentFn;
      };
      if (models.__origGenerateContent) return;

      const orig = models.generateContent.bind(models) as GenerateContentFn;
      models.__origGenerateContent = orig;
      models.generateContent = ((args: Parameters<GenerateContentFn>[0]) => {
        const model = String(args.model ?? GEMINI_PRO_MODEL);
        if (traceDepth > 0) {
          return orig(args);
        }
        return tracedGeminiCall(model, () => orig(args), {
          stage: 'generateContent',
        });
      }) as GenerateContentFn;
      g[sentinel] = true;
    } catch (err) {
      console.warn('[tracedGeminiCall] install failed:', (err as Error)?.message);
    }
  })();
}

// Browser + server: install tracer at module load.
installGeminiCallTracer();
